<app-page-shell variant="default">
  <div class="environment-info-page">
    <div class="environment-info-header">
      <div>
        <p class="environment-info-eyebrow">{{ eyebrow }}</p>
        <h1>Environment Information</h1>
        <p class="environment-info-intro">
          Review these restrictions and operating notes before running actions in this environment.
        </p>
      </div>
      @if (info()) {
      <span class="environment-info-label">{{ info().environment.label }}</span>
      }
    </div>

    @if (loading()) {
    <div class="environment-info-state" role="status">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      Loading environment information...
    </div>
    }

    @if (error() && !loading()) {
    <div class="environment-info-state environment-info-state--error" role="alert">
      <span>{{ error() }}</span>
      <button type="button" class="btn btn-outline-danger btn-sm" (click)="load()">Retry</button>
    </div>
    }

    @if (info() && !loading()) {
      <section aria-labelledby="environment-restrictions-title">
        <h2 id="environment-restrictions-title">Server restrictions</h2>
        <div class="environment-info-grid">
          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Email delivery</h3>
              <span class="environment-badge" [class]="'environment-badge--' + info().restrictions.emailDelivery.mode">
                {{ modeLabel(info().restrictions.emailDelivery.mode) }}
              </span>
            </div>
            @if (info().restrictions.emailDelivery.mode === 'disabled') {
            <p>
              Outbound email delivery is disabled. Campaign and system emails will not be sent.
            </p>
            }
            @if (info().restrictions.emailDelivery.mode === 'restricted') {
            <p>
              Only recipients matching the following patterns can receive email. Other recipients are skipped.
            </p>
            }
            @if (info().restrictions.emailDelivery.mode === 'unrestricted') {
            <p>
              Outbound email is enabled without a recipient whitelist. Confirm recipients before sending.
            </p>
            }
            @if (info().restrictions.emailDelivery.allowedRecipients.length) {
            <div class="environment-chip-list">
              @for (recipient of info().restrictions.emailDelivery.allowedRecipients; track recipient) {
              <span
                class="environment-chip">
                {{ recipient }}
              </span>
              }
            </div>
            }
          </article>

          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Registration platform push</h3>
              <span
                class="environment-badge"
                [class]="'environment-badge--' + info().restrictions.registrationPlatformPush.mode">
                {{ modeLabel(info().restrictions.registrationPlatformPush.mode) }}
              </span>
            </div>
            @if (info().restrictions.registrationPlatformPush.mode === 'restricted') {
            <p>
              Share-link push is allowed only for the listed project IDs. Other project pushes are skipped and logged.
            </p>
            }
            @if (info().restrictions.registrationPlatformPush.mode === 'unrestricted') {
            <p>
              No project whitelist is configured. Sync may push share links to connected external platforms.
            </p>
            }
            @if (info().restrictions.registrationPlatformPush.allowedProjectIds.length) {
            <div
              class="environment-chip-list">
              @for (projectId of info().restrictions.registrationPlatformPush.allowedProjectIds; track projectId) {
              <span
                class="environment-chip">
                Project {{ projectId }}
              </span>
              }
            </div>
            }
          </article>

          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Campaign sender domains</h3>
              <span
                class="environment-badge"
                [class]="'environment-badge--' + info().restrictions.campaignSenderDomains.mode">
                {{ modeLabel(info().restrictions.campaignSenderDomains.mode) }}
              </span>
            </div>
            @if (info().restrictions.campaignSenderDomains.mode === 'restricted') {
            <p>
              Users without the restricted-domain permission cannot use these domains for From or Reply-To addresses.
            </p>
            }
            @if (info().restrictions.campaignSenderDomains.mode === 'unrestricted') {
            <p>
              No restricted campaign sender domains are configured.
            </p>
            }
            @if (info().restrictions.campaignSenderDomains.restrictedDomains.length) {
            <div
              class="environment-chip-list">
              @for (domain of info().restrictions.campaignSenderDomains.restrictedDomains; track domain) {
              <span
                class="environment-chip">
                {{ domain }}
              </span>
              }
            </div>
            }
          </article>

          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Domain verification</h3>
              <span
                class="environment-badge"
                [class]="'environment-badge--' + info().restrictions.domainVerification.mode">
                {{ modeLabel(info().restrictions.domainVerification.mode) }}
              </span>
            </div>
            @if (info().restrictions.domainVerification.allowedDomains.length) {
            <p>
              Only the listed sender domains are verified. Verifying any other domain is skipped and changes nothing, and its relay and provider settings cannot be switched.
            </p>
            }
            @if (!info().restrictions.domainVerification.allowedDomains.length) {
            <p>
              Every sender domain can be verified.
            </p>
            }
            @if (info().restrictions.domainVerification.allowedDomains.length) {
            <div
              class="environment-chip-list">
              @for (domain of info().restrictions.domainVerification.allowedDomains; track domain) {
              <span
                class="environment-chip">
                {{ domain }}
              </span>
              }
            </div>
            }
          </article>

          @for (restriction of info().restrictions.additional; track trackRestriction($index, restriction)) {
          <article
            class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>{{ restriction.label }}</h3>
              <span
                class="environment-badge"
                [class]="'environment-badge--' + restriction.status">
                {{ infoStatusLabel(restriction.status) }}
              </span>
            </div>
            <p>{{ restriction.description }}</p>
            @if (restriction.values.length) {
            <div class="environment-chip-list">
              @for (value of restriction.values; track value) {
              <span class="environment-chip">
                {{ value }}
              </span>
              }
            </div>
            }
          </article>
          }
        </div>
      </section>

      @if (info().workflows.length) {
      <section aria-labelledby="environment-workflows-title">
        <h2 id="environment-workflows-title">Workflow guidance</h2>
        <div class="environment-info-grid">
          @for (workflow of info().workflows; track trackWorkflow($index, workflow)) {
          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>{{ workflow.label }}</h3>
              <span class="environment-badge" [class]="'environment-badge--' + workflow.status">
                {{ statusLabel(workflow.status) }}
              </span>
            </div>
            <p>{{ workflow.description }}</p>
            @if (workflow.values.length) {
            <div class="environment-chip-list">
              @for (value of workflow.values; track value) {
              <span class="environment-chip">
                {{ value }}
              </span>
              }
            </div>
            }
          </article>
          }
        </div>
      </section>
      }

      @if (info().notices.length) {
      <section aria-labelledby="environment-notices-title">
        <h2 id="environment-notices-title">Additional notices</h2>
        <div class="environment-notices">
          @for (notice of info().notices; track trackNotice($index, notice)) {
          <article
            class="environment-notice"
            [class]="'environment-notice--' + notice.severity">
            <h3>{{ notice.title }}</h3>
            <p>{{ notice.description }}</p>
            @if (notice.values.length) {
            <div class="environment-chip-list">
              @for (value of notice.values; track value) {
              <span class="environment-chip">
                {{ value }}
              </span>
              }
            </div>
            }
          </article>
          }
        </div>
      </section>
      }

      @if (info().updatedAt) {
      <p class="environment-info-updated">
        Guidance last updated {{ info().updatedAt | date:'medium' }}
      </p>
      }
    }
  </div>
</app-page-shell>
